import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Typography, Button, Chip, CircularProgress, Grid, Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { toast } from "react-toastify";

import { getMarketplaceItem } from "../../services/appMarketplaceService";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    width: "100%",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  pageTitle: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a2e",
    marginBottom: theme.spacing(0.5),
  },
  pageSubtitle: {
    fontSize: "14px",
    color: "#6b7280",
  },
  content: {
    background: "#ffffff",
    borderRadius: 12,
    padding: theme.spacing(3),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  },
  coverImg: {
    width: "100%",
    maxHeight: 340,
    objectFit: "cover",
    borderRadius: 10,
    marginBottom: 20,
    boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
  },
  coverPlaceholder: {
    width: "100%",
    height: 240,
    borderRadius: 10,
    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  placeholderInitials: {
    color: "#fff",
    fontWeight: 800,
    fontSize: 56,
    opacity: 0.85,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    color: "#1e293b",
    marginBottom: 10,
    marginTop: 20,
    paddingBottom: 6,
    borderBottom: "2px solid #f1f5f9",
  },
  galleryGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  galleryThumb: {
    width: 130,
    height: 90,
    objectFit: "cover",
    borderRadius: 8,
    cursor: "pointer",
    border: "2px solid transparent",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    transition: "border-color 0.15s, transform 0.15s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      transform: "scale(1.04)",
    },
  },
  videoWrapper: {
    position: "relative",
    paddingTop: "56.25%",
    borderRadius: 10,
    overflow: "hidden",
    background: "#000",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  },
  videoIframe: {
    position: "absolute",
    top: 0, left: 0,
    width: "100%", height: "100%",
    border: "none",
  },
  descriptionHtml: {
    fontSize: "0.95rem",
    lineHeight: 1.8,
    color: "#334155",
    "& p": { marginBottom: 10 },
    "& ul, & ol": { paddingLeft: 22, marginBottom: 10 },
    "& h1, & h2, & h3": { marginBottom: 8, color: "#1e293b", fontWeight: 700 },
    "& strong": { color: "#1e293b" },
    "& li": { marginBottom: 4 },
  },
  ctaBox: {
    background: "#f0f9ff",
    borderRadius: 10,
    padding: "20px 24px",
    border: "1px solid #bae6fd",
    marginTop: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
  },
  sidebar: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    position: "sticky",
    top: 20,
  },
  sidebarTitle: {
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 16,
    fontSize: "1rem",
  },
  sidebarLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    display: "block",
    marginBottom: 4,
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: "64px 0",
  },
}));

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    let vid = u.searchParams.get("v");
    if (!vid && u.hostname === "youtu.be") vid = u.pathname.slice(1);
    if (!vid && u.pathname.includes("/shorts/")) vid = u.pathname.split("/shorts/")[1];
    if (vid) return `https://www.youtube.com/embed/${vid}`;
  } catch {}
  return null;
};

const AppMarketplaceDetail = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMarketplaceItem(id)
      .then(({ data }) => setItem(data))
      .catch(() => {
        toast.error("App não encontrado");
        history.push("/app-marketplace");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const embedUrl = item ? getYoutubeEmbedUrl(item.videoUrl) : null;

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/app-marketplace")}
            style={{ textTransform: "none", fontWeight: 600 }}
          >
            Marketplace
          </Button>
          {item && (
            <Box>
              <Typography className={classes.pageTitle}>{item.title}</Typography>
              {item.category && (
                <Typography className={classes.pageSubtitle}>{item.category}</Typography>
              )}
            </Box>
          )}
        </div>
        {item?.buttonUrl && (
          <Button
            variant="contained"
            color="primary"
            endIcon={<OpenInNewIcon />}
            onClick={() => window.open(item.buttonUrl, "_blank")}
            style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}
          >
            {item.buttonText || "Saiba Mais"}
          </Button>
        )}
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className={classes.content}>
          <div className={classes.loadingBox}>
            <CircularProgress />
          </div>
        </div>
      ) : item ? (
        <div className={classes.content}>
          <Grid container spacing={4}>
            {/* Coluna principal */}
            <Grid item xs={12} md={8}>
              {item.coverImage ? (
                <img
                  src={`${backendUrl}/public/${item.coverImage}`}
                  alt={item.title}
                  className={classes.coverImg}
                />
              ) : (
                <div className={classes.coverPlaceholder}>
                  <Typography className={classes.placeholderInitials}>
                    {item.title.substring(0, 2).toUpperCase()}
                  </Typography>
                </div>
              )}

              {item.category && (
                <Chip label={item.category} size="small" color="primary" style={{ marginBottom: 14 }} />
              )}

              {item.shortDescription && (
                <Typography style={{ color: "#475569", fontSize: "1.05rem", marginBottom: 16, lineHeight: 1.6 }}>
                  {item.shortDescription}
                </Typography>
              )}

              {item.description && (
                <>
                  <Typography className={classes.sectionTitle}>Sobre</Typography>
                  <div
                    className={classes.descriptionHtml}
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                </>
              )}

              {item.gallery && item.gallery.length > 0 && (
                <>
                  <Typography className={classes.sectionTitle}>Galeria</Typography>
                  <div className={classes.galleryGrid}>
                    {item.gallery.map((img) => (
                      <img
                        key={img}
                        src={`${backendUrl}/public/${img}`}
                        alt=""
                        className={classes.galleryThumb}
                        onClick={() => window.open(`${backendUrl}/public/${img}`, "_blank")}
                      />
                    ))}
                  </div>
                </>
              )}

              {embedUrl && (
                <>
                  <Typography className={classes.sectionTitle}>Vídeo</Typography>
                  <div className={classes.videoWrapper}>
                    <iframe
                      src={embedUrl}
                      title={item.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className={classes.videoIframe}
                    />
                  </div>
                </>
              )}

              {item.videoFile && !embedUrl && (
                <>
                  <Typography className={classes.sectionTitle}>Vídeo</Typography>
                  <video
                    src={`${backendUrl}/public/${item.videoFile}`}
                    controls
                    style={{ width: "100%", borderRadius: 10 }}
                  />
                </>
              )}

              {item.buttonUrl && (
                <div className={classes.ctaBox}>
                  <Box>
                    <Typography style={{ fontWeight: 700, color: "#0c4a6e" }}>
                      Pronto para começar?
                    </Typography>
                    <Typography variant="body2" style={{ color: "#0369a1", marginTop: 2 }}>
                      Clique no botão para saber mais sobre este serviço.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<OpenInNewIcon />}
                    onClick={() => window.open(item.buttonUrl, "_blank")}
                    style={{ textTransform: "none", fontWeight: 700, borderRadius: 8, padding: "10px 24px" }}
                  >
                    {item.buttonText || "Saiba Mais"}
                  </Button>
                </div>
              )}
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              <div className={classes.sidebar}>
                <Typography className={classes.sidebarTitle}>Informações</Typography>
                {item.category && (
                  <Box mb={2}>
                    <span className={classes.sidebarLabel}>Categoria</span>
                    <Chip label={item.category} size="small" color="primary" />
                  </Box>
                )}
                {item.buttonUrl && (
                  <Box mt={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => window.open(item.buttonUrl, "_blank")}
                      style={{ textTransform: "none", fontWeight: 700, borderRadius: 8 }}
                    >
                      {item.buttonText || "Saiba Mais"}
                    </Button>
                  </Box>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      ) : null}
    </div>
  );
};

export default AppMarketplaceDetail;
